import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';

import { useGradeFlow } from '@/states/grade-flow';

import { Badge } from '@/components/ui/badge';
import { InsightsThumbnails } from '@/components/insights/insights-thumbnails';

import { PearEyeQuestions } from './pear-eye-questions';
import { ReadableImages } from '../readable-images';

type DiabeticQuestionsProps = {
  mode?: 'grading' | 'insights';
};

export function DiabeticQuestions({ mode = 'grading' }: DiabeticQuestionsProps) {
  const { t } = useTranslation();

  const { hasLaterality } = useGradeFlow(
    useShallow((s) => ({ hasLaterality: s.isLateralityPresentOnImgs }))
  );

  if (!hasLaterality) {
    return (
      <div className="flex-1 w-full flex flex-col items-start">
        {mode === 'grading' && <ReadableImages size="sm" displayTitle={false} />}
        {mode === 'insights' && <InsightsThumbnails size="sm" />}
        <PearEyeQuestions laterality="both" />
      </div>
    );
  }

  return (
    <>
      <h2 className="mb-4 md:mb-8 font-bold">{t('grade-diabetic-retinopathy-questions-title')}</h2>

      <div className="flex-1 flex flex-col lg:flex-row justify-between max-w-full mb-8">
        <div className="pr-4 flex flex-col items-start shrink-0 grow lg:w-[48%] mb-12">
          <Badge color="blue" className="mb-4">
            {t('right')}
          </Badge>
          {mode === 'grading' && <ReadableImages filter="right" size="sm" displayTitle={false} />}
          {mode === 'insights' && <InsightsThumbnails filter="right" size="sm" />}
          <PearEyeQuestions laterality="right" />
        </div>
        <div className="w-px bg-border" />
        <div className="flex flex-col items-start shrink-0 grow lg:w-[48%] lg:pl-4 mb-12">
          <Badge color="blue" className="mb-4">
            {t('left')}
          </Badge>
          {mode === 'grading' && <ReadableImages filter="left" size="sm" displayTitle={false} />}
          {mode === 'insights' && <InsightsThumbnails filter="left" size="sm" />}
          <PearEyeQuestions laterality="left" />
        </div>
      </div>
    </>
  );
}
